import { useTranslation } from "react-i18next";
import { Radio, Skeleton } from "@thetreep/ui";
import {
  PlacementChoiceResponse,
  PlacementKind,
  SegmentPlacement,
  Solution,
} from "@gen/travel/v1/search_pb";
import PlacementPreference from "./PlacementPreference";
import PlacementNextRelatives from "./PlacementNextRelatives";
import { useState } from "react";
import { ExchangeSolution } from "@gen/travel/v1/exchange_pb";
import { useBookingStore } from "@stores/useBookingStore";
import PlacementSeatMap from "./PlacementSeatMap";
import { Segment } from "@gen/travel/v1/common_pb";
import TravelTimeline from "@components/common/Timeline/TravelTimeline";
import { GhgTag } from "@components/common/Timeline/GHGTag";
import FloatingTooltip from "@components/common/FloatingTooltip";

type PlacementProps = {
  solution: Solution | ExchangeSolution;
  options: PlacementChoiceResponse | undefined;
};

export default function PlacementChoices({
  solution,
  options,
}: PlacementProps) {
  const journey = solution.journey;
  const [isLoading, setIsLoading] = useState<boolean>(
    () => !options?.placements,
  );

  const { searchRequest } = useBookingStore();

  const nbPassengers = (searchRequest?.additionalPassengers?.length ?? 0) + 1;

  const [prevOptions, setPrevOptions] = useState(options);
  if (options !== prevOptions) {
    setPrevOptions(options);
    if (options?.placements) {
      setIsLoading(false);
    }
  }

  if (!journey || !journey.schedule?.arrival) {
    return;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-12">
        <Skeleton animation height="150px" width="280px" />
        <Skeleton animation height="300px" width="100%" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-12">
      {options?.placements.map((options) => (
        <PlacementForSegment
          key={options.segmentId}
          segment={journey.segments.find((s) => s.id === options.segmentId)}
          options={options}
          numPassengers={nbPassengers}
        />
      ))}
    </div>
  );
}

interface PlacementForSegmentProps {
  segment: Segment | undefined;
  options: SegmentPlacement;
  numPassengers: number;
}

// Display placement options for each section of this outward or inward journey.
function PlacementForSegment({
  segment,
  options,
  numPassengers,
}: PlacementForSegmentProps) {
  const [selectedOption, setSelectedOption] = useState<PlacementKind>(
    PlacementKind.UNSPECIFIED,
  );

  if (!segment) return;
  return (
    <div className="flex flex-col justify-between gap-4">
      <div className="border border-gray-blue-300 bg-white self-start rounded-lg p-6">
        <TravelTimeline journey={segment}>
          <div className="flex justify-center">
            <GhgTag value={segment.ghgEmission} />
            {segment.transport?.logo && (
              <FloatingTooltip content={segment.transport.name}>
                <img
                  src={segment.transport.logo}
                  alt="carrier-logo"
                  className="ml-1 max-w-[80px]"
                />
              </FloatingTooltip>
            )}
          </div>
        </TravelTimeline>
      </div>
      <>
        <CustomRadio
          label="placement.noPreference"
          value={PlacementKind.UNSPECIFIED}
          checked={selectedOption === PlacementKind.UNSPECIFIED}
          onClick={() => setSelectedOption(PlacementKind.UNSPECIFIED)}
        />
        {options.availablePlacement?.closeTo && (
          <CustomRadio
            label="placement.nextRelatives"
            value={PlacementKind.CLOSE_TO}
            checked={selectedOption === PlacementKind.CLOSE_TO}
            onClick={() => setSelectedOption(PlacementKind.CLOSE_TO)}
          >
            <PlacementNextRelatives
              onConfirm={() => {}}
              segmentId={options.segmentId}
            />
          </CustomRadio>
        )}
        {options.availablePlacement?.seatMap && (
          <CustomRadio
            label="placement.trainPlan"
            value={PlacementKind.SEAT_MAP}
            checked={selectedOption === PlacementKind.SEAT_MAP}
            onClick={() => setSelectedOption(PlacementKind.SEAT_MAP)}
          >
            <PlacementSeatMap
              key={options.segmentId}
              transportNumber={segment.transport?.number}
              passengerNumber={numPassengers}
              placementCapability={options.placementCapabilities}
              segmentId={options.segmentId}
            />
          </CustomRadio>
        )}
        {options.availablePlacement?.placementPreferences && (
          <CustomRadio
            label="placement.preference"
            value={PlacementKind.PREFERENCES}
            checked={selectedOption === PlacementKind.PREFERENCES}
            onClick={() => setSelectedOption(PlacementKind.PREFERENCES)}
          >
            <PlacementPreference
              placementCapability={options.placementCapabilities}
              segmentId={options.segmentId}
            />
          </CustomRadio>
        )}
      </>
    </div>
  );
}

interface CustomRadioProps {
  value: PlacementKind;
  label: string;
  checked?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
}

// Custom radio button within a card.
function CustomRadio({
  value,
  checked,
  label,
  onClick,
  children,
}: CustomRadioProps) {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-col bg-white p-4 rounded-lg border border-gray-blue-300"
      onClick={() => onClick && onClick()}
    >
      <div className="flex items-center gap-2 cursor-pointer">
        <Radio
          value={value.toString()}
          checked={checked}
          onChange={() => onClick && onClick()}
        />
        <span>{t(label)}</span>
      </div>
      {checked && children}
    </div>
  );
}
